// polyfills
import "core-js/stable";
import "regenerator-runtime/runtime";

const _ = require('underscore');
var fetch = require('isomorphic-fetch');

export function ServerError(message, status) {
	this.name = "ServerError";
	this.message = message;
	this.status = status;
}
ServerError.prototype = Object.create(Error.prototype);
ServerError.prototype.constructor = ServerError;
ServerError.prototype.toString = function () {
	return "Error " + this.status + " - " + this.message;
};

export function ApiError(message, status) {
	this.name = "ApiError";
	this.message = message;
	this.status = status;
}
ApiError.prototype = Object.create(Error.prototype);
ApiError.prototype.constructor = ApiError;
ApiError.prototype.toString = function () {
	return "Error " + this.status + " - " + this.message;
};

export function JobError(message) {
	this.name = "JobError";
	this.message = message;
}
JobError.prototype = Object.create(Error.prototype);
JobError.prototype.constructor = JobError;
JobError.prototype.toString = function () {
	return this.message;
};

// SETUP_API is a cross dispatcher action that stores information recieved
// from the backend dispatcher so that the api class can dip into the store and
// find the correct endpoints depending on the apiNamespace
export const SETUP_API = 'SETUP_API';
export function setupAPI(dispatchers, api_auth) {
	return {
		type: SETUP_API,
		dispatchers: dispatchers,
		auth: api_auth
	};
}

/**
 *
 * @param time
 * @returns {Promise}
 */
function sleep(time) {
	return new Promise(resolve => setTimeout(resolve, time));
}

/**
 * apiName is used to find the correct endpoint from the state store under
 * `store.api.dispatchers.${apiName}`
 *
 * @param name - string name of the api, used for finding the api endpoint
 * @returns {{call: call, waitForSuccess: waitForSuccess}}
 */
export function create(name) {

	const apiName = name;

	function call(getState, uri, method, payload) {

		var options = {
			credentials: 'same-origin',
			method: method,
			headers: {
				Accept: 'application/json'
			}
		};

		let fullURI = "";
		// check if uri is a relative or full uri
		if (uri.indexOf('://') > 0 || uri.indexOf('//') === 0) {
			fullURI = uri;
		} else {
			fullURI = `${getState().api.dispatchers[apiName]}${uri}`;
		}

		// POST actions are the only actions that could possible change the state
		// and should therefore be protected by CSRF. GET/HEAD operations are
		// prefered to actions that doesn't change state.
		if (method.toLowerCase() === 'post') {
			const body = payload || {};
			const tokenName = `${getState().api.auth.name}`;
			body[tokenName] = `${getState().api.auth.value}`;
			options.body = JSON.stringify(body);
		}

		return fetch(fullURI, options)
			.then(function(response) {

				// no content, can't do anything further for this successful status
				if (response.status === 204) {
					return;
				}

				// the http response is in the 200 >=  <= 299 range
				if (response.ok) {
					// If the response is to login, such as the user session has timed out, then
					// reload the current page so they can login again.
					if (/Security\/login/.test(response.url)) {
						return location.reload();
					}

					return response.json().then(json => json);
				}

				// reload the page on unauthorised, this will ensure if the session has
				// expired then we don't get repeated 401 from api being called repeatedly without a session
				if (response.status === 401) {
					return location.reload();
				}

				// if the status code is outside of 200 - 299 we try to parse
				// the error that can either be some unexpected error or a nicer
				// API error response

				// this is unexpected, so just throw it without trying to parse
				// the body of the response
				if (response.status >= 500) {
					const err = new ServerError(response.statusText, response.status);
					console.error(err); // eslint-disable-line no-console
					throw err;
				}

				// try parsing the content of the page in hope that there is a
				// nice json formatted error message
				return response.json()
					.then(function(json) {
						if (json.errors) {
							throw new ApiError(json.errors[0].title, json.errors[0].status_code || response.status);
						} else if (json.message) {
							throw new ApiError(json.message, json.meta.status_code || response.status);
						}
						// there isn't a custom error message, so fallback to
						// the HTTP response
						throw new ApiError(response.statusText, response.status);
					})
					.catch(function(err) {
						console.error(err); // eslint-disable-line no-console
						if (err.name === 'ApiError') {
							throw err;
						}
						// if we got here the, response body couldn't be parsed
						// as JSON at all, use the HTTP response data
						throw new ApiError(response.statusText, response.status);
					});
			})
			.catch(function(err) {
				console.error('There has been a problem with your fetch operation:', err);
			});
	}

	// Waits for a specific healthy response
	function waitForSuccess(getState, uri, retryInterval, callback) {
		var retry = retryInterval || 100;
		if (retry > 5000) {
			retry = 5000;
		}
		return call(getState, uri, 'get').then((data) => {
			if (callback) {
				callback(data);
			}
			let status = 'Status missing';
			if (_.has(data, 'status')) {
				status = data.status;
			} else if (_.has(data, 'data') && _.has(data.data, 'attributes') && _.has(data.data.attributes, 'status')) {
				status = data.data.attributes.status;
			}
			switch (status) {
				case 'Completed':
				case 'Complete':
				case 'Submitted':
					return data;
				case 'Status missing':
					throw new JobError("Could not obtain status for the job.");
				case 'Failed':
					if (_.has(data.data.attributes, 'error')) {
						throw new JobError(data.data.attributes.error);
					}
					throw new JobError("The job has failed.");
				default:
					return sleep(retry).then(() => waitForSuccess(getState, uri, 2 * retry, callback));
			}
		});
	}

	// Waits for a response, doesn't care what it is
	function waitForResponse(getState, uri, retryInterval, callback) {
		var retry = retryInterval || 100;
		if (retry > 5000) {
			retry = 5000;
		}
		return call(getState, uri, 'get').then((data) => {
			if (callback) {
				callback(data);
			}

			if (data) {
				return data;
			}

			return sleep(retry).then(() => waitForResponse(getState, uri, 2 * retry, callback));
		});
	}

	return {
		call,
		waitForSuccess,
		waitForResponse
	};
}
